import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { fmt } from "./constants";

// Normaliza texto: minúsculas + sin acentos, para que "gráficos" y "graficos"
// hagan match indistintamente.
const norm = (s) => (s || "").toString()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

export function ProductPicker({ products, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = products.find((p) => p.id === value);
  const inStock = products.filter((p) => p.stock > 0);

  const filtered = useMemo(() => {
    const query = q.trim();
    if (!query) return inStock;
    // Multi-token: cada palabra separada por espacio debe aparecer en el
    // "haystack" combinado del producto. Así "dell latitude 16gb" encuentra
    // el laptop aunque las palabras estén en campos distintos y en cualquier
    // orden.
    const tokens = norm(query).split(/\s+/).filter(Boolean);
    const scored = inStock
      .map((p) => {
        const name = norm(p.name);
        const brand = norm(p.brand);
        const sku = norm(p.sku);
        const chars = norm(p.characteristics);
        const cat = norm(p.category);
        const haystack = [name, brand, sku, chars, cat].join(" ");
        const allMatch = tokens.every((t) => haystack.includes(t));
        if (!allMatch) return null;
        // Score simple: prioriza matches en nombre > marca > sku > chars > cat.
        let score = 0;
        tokens.forEach((t) => {
          if (name.includes(t)) score += 10;
          else if (brand.includes(t)) score += 6;
          else if (sku.includes(t)) score += 5;
          else if (chars.includes(t)) score += 3;
          else if (cat.includes(t)) score += 1;
          // Match exacto en el nombre → bonus
          if (name === t) score += 20;
          if (name.startsWith(t)) score += 5;
        });
        return { p, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    return scored.map((x) => x.p);
  }, [q, inStock]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 w-full flex items-center justify-between gap-2 rounded-md border border-zinc-300 bg-white px-3 h-9 text-sm hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
          data-testid={testId || "product-picker"}
        >
          <span className="truncate text-left">
            {selected ? (
              <>
                <span className="font-medium">{selected.name}</span>
                {selected.brand && <span className="text-zinc-500"> · {selected.brand}</span>}
                <span className="text-zinc-400 ml-1">(stock: {selected.stock})</span>
              </>
            ) : (
              <span className="text-zinc-400">Buscar producto…</span>
            )}
          </span>
          <MagnifyingGlass size={14} className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px]" align="start">
        <div className="relative p-2 border-b border-zinc-100">
          <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Escribe varias palabras: dell latitude 16gb…"
            className="pl-8 h-9 text-sm"
            data-testid="product-picker-search"
          />
          {q && (
            <div className="text-[10px] text-zinc-400 mt-1 px-1">
              {filtered.length} coincidencia{filtered.length === 1 ? "" : "s"} · busca por nombre + marca + SKU + características + categoría
            </div>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto" data-testid="product-picker-list">
          {filtered.length === 0 && (
            <div className="text-center text-xs text-zinc-400 py-8">
              {q ? "Sin coincidencias" : "No hay productos con stock"}
            </div>
          )}
          {filtered.map((p) => {
            const low = p.stock <= (p.low_stock_threshold || 0);
            const priceToShow = p.store_price || p.marketplace_price || p.min_price || 0;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 ${value === p.id ? "bg-[#002FA7]/5" : ""}`}
                data-testid={`product-picker-item-${p.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {p.name}
                      {p.aesthetic_grade && (
                        <span
                          className={`inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-heading font-extrabold shrink-0 ${
                            p.aesthetic_grade === "A" ? "bg-emerald-100 text-emerald-700" :
                            p.aesthetic_grade === "B" ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-[#E0000F]"
                          }`}
                          title={`Estética ${p.aesthetic_grade}`}
                        >
                          {p.aesthetic_grade}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {p.brand ? `${p.brand} · ` : ""}{p.category}{p.sku ? ` · SKU ${p.sku}` : ""}
                    </div>
                    {p.characteristics && (
                      <div className="text-[10px] text-zinc-400 truncate">{p.characteristics}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xs font-semibold numeric ${low ? "text-[#E0000F]" : "text-zinc-700"}`}>
                      stock: {p.stock}
                    </div>
                    <div className="text-[11px] text-[#002FA7] font-semibold numeric" title="Precio de tienda (base)">
                      {fmt(priceToShow, p.sale_currency || "MXN")}
                      <span className="text-[9px] text-zinc-400 font-normal ml-0.5">tienda</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
